import { DeliveryAreaItem } from '@/services/deliveryAreas/types'
import { REGEX } from '@/utils/consts'
import { ValueSetterParams } from 'ag-grid-community'
import { RefObject } from 'react'
import { DA_CITY_WITH_NULLABLE_DISTRICT, DA_MAX_SIZE_DISTRICT } from '../components/consts'
import MToast from '@/utils/modal/MToast'
import { inRange } from 'lodash-es'
import { AgGridReact } from 'ag-grid-react'
import { ConfirmModal } from '@/components/content/ConfirmModal'
import Button from '@/components/content/Button'
import MRender from '@/utils/modal/MRender'
import { DeliveryLeadDayConfirmModal, RemoteAreaConfirmModal } from '../components/BulkUpdateModal'

/**
 *  @hook useCellChangeValidation
 *  - 셀 변경시 validation 진행 -> 케이스에 맞는 Confirm 노출
 */
export const useCellChangeValidation = ({ $grid }: { $grid: RefObject<AgGridReact> }) => {
  /** 구구분, 읍면동 update validation check */
  const handleCheckTownAndDistrict = ({ column, newValue, data }: ValueSetterParams) => {
    const colId = column.getColId() as keyof DeliveryAreaItem
    if (colId !== 'town' && colId !== 'district') return false

    const trimmedValue = typeof newValue === 'string' ? newValue.trim() : newValue

    // 세종시는 구구분 빈값 허용 그 외엔 불가
    const isNullableDistrict = colId === 'district' && data.city === DA_CITY_WITH_NULLABLE_DISTRICT
    if (!isNullableDistrict && !trimmedValue) {
      MToast.error(`한글자 이상 입력해주세요`)
      return false
    }

    const isRegExError = !isNullableDistrict && !REGEX.koreanAndNumber.test(trimmedValue)
    const isMaxLengthError = trimmedValue?.length > DA_MAX_SIZE_DISTRICT

    if (isRegExError || isMaxLengthError) {
      const errorMessage = isRegExError ? '한글, 한글+숫자만 입력 가능해요.' : '최대 50자까지 입력할 수 있어요.'

      MRender.show(
        <ConfirmModal
          title={errorMessage}
          footer={
            <Button fit kind="primary_light" onClick={() => MRender.destory()}>
              확인
            </Button>
          }
        />,
      )
      return false
    }

    data[colId] = trimmedValue
    return true
  }

  /** 배송리드타임 update validation check */
  const handleCheckReadDay = ({ column, newValue, data }: ValueSetterParams) => {
    const colId = column.getColId() as keyof DeliveryAreaItem
    if (colId !== 'deliveryLeadDay' || typeof newValue !== 'number') return false

    const deliveryLeadDay = newValue
    const remoteArea = data.remoteArea

    if (deliveryLeadDay < 0 || deliveryLeadDay > 9) {
      MToast.error('리드타임은 9 이하로 입력해 주세요.')
      return false
    }

    // 0일, 4일이상으로 변경, 도서산간 N인 케이스
    const isChangeError = (deliveryLeadDay === 0 || deliveryLeadDay >= 4) && !remoteArea

    if (isChangeError) {
      MRender.show(
        <DeliveryLeadDayConfirmModal
          deliveryLeadDay={deliveryLeadDay}
          onClose={MRender.destory}
          onConfirm={() => {
            submitDeliveryLeadDayUpdate({ id: data.id, deliveryLeadDay })
            MRender.destory()
          }}
        />,
      )
      return false
    }

    data.deliveryLeadDay = deliveryLeadDay
    return true
  }

  const submitDeliveryLeadDayUpdate = ({ id, deliveryLeadDay }: { id: number; deliveryLeadDay: number }) => {
    const rowNode = $grid.current?.api.getRowNode(id.toString())
    if (!rowNode) return

    rowNode.data.deliveryLeadDay = deliveryLeadDay
    rowNode.data.remoteArea = true
    rowNode.setSelected(true)

    $grid.current?.api.redrawRows({ rowNodes: [rowNode] })
  }

  /** 도서산간여부 update validation check */
  const handleCheckRemoteArea = ({ column, newValue, data }: ValueSetterParams) => {
    const colId = column.getColId() as keyof DeliveryAreaItem
    if (colId !== 'remoteArea' || typeof newValue !== 'boolean') return false

    const remoteArea = newValue
    const isReadDay1to3 = inRange(data.deliveryLeadDay, 1, 4)

    // 리드타임 1~3이 아닌경우, N으로 변경 불가
    if (!remoteArea && !isReadDay1to3) {
      const rowNode = $grid.current?.api.getRowNode(data.id)
      $grid.current?.api.redrawRows({ rowNodes: [rowNode!] })

      MRender.show(<RemoteAreaConfirmModal onConfirm={() => MRender.destory()} />)
      return false
    }

    data.remoteArea = remoteArea
    return true
  }

  return {
    handleCheckTownAndDistrict,
    handleCheckReadDay,
    handleCheckRemoteArea,
  }
}
